"use client";

import { useEffect, useRef, useState } from "react";
import { playResultSound } from "@/lib/sounds";

interface ScoreDisplayProps {
  score: number;
  rank: string;
  alignmentLabel: string | null;
  hasCrown?: boolean;
  // Sticky offset from the top. Defaults to the very top; the demo screen
  // pushes it below its persistent banner.
  topClass?: string;
}

interface FloatingDelta {
  id: number;
  delta: number;
}

let floatCounter = 0;

export default function ScoreDisplay({
  score,
  rank,
  alignmentLabel,
  hasCrown,
  topClass = "top-0",
}: ScoreDisplayProps) {
  const [flash, setFlash] = useState<"green" | "red" | null>(null);
  const [floats, setFloats] = useState<FloatingDelta[]>([]);
  const [display, setDisplay] = useState(score);
  const prevScore = useRef(score);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const delta = score - prevScore.current;
    const from = prevScore.current;
    prevScore.current = score;

    if (delta === 0) {
      setDisplay(score);
      return;
    }

    const positive = delta > 0;
    setFlash(positive ? "green" : "red");
    playResultSound(positive);

    // Spawn a floating +/- delta next to the score.
    const id = ++floatCounter;
    setFloats((prev) => [...prev, { id, delta }]);

    // Count the number up/down to the new total for a satisfying tally.
    const startTime = performance.now();
    const duration = 600;
    const step = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(from + (score - from) * eased));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(step);

    const flashTimer = setTimeout(() => setFlash(null), 800);
    const floatTimer = setTimeout(
      () => setFloats((prev) => prev.filter((f) => f.id !== id)),
      1200,
    );

    return () => {
      clearTimeout(flashTimer);
      clearTimeout(floatTimer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [score]);

  return (
    <div
      className={`sticky ${topClass} z-30 w-full border-b border-white/10 bg-background/95 py-2 pl-4 pr-24 text-left md:px-4 md:py-[clamp(0.75rem,2.5vh,1.5rem)] md:text-center ${
        flash === "green"
          ? "animate-flash-green"
          : flash === "red"
            ? "animate-flash-red"
            : ""
      }`}
    >
      <div className="relative inline-block">
        <div className="text-[1.75rem] font-extrabold tracking-tight md:text-[clamp(2rem,6vw,4.5rem)]">
          {display.toLocaleString()}
        </div>
        {/* Floating +/- deltas rising off the score */}
        <div className="pointer-events-none absolute left-full top-1 ml-3 w-32 text-left">
          {floats.map((f) => (
            <div
              key={f.id}
              className={`animate-float-up absolute font-mono text-[clamp(1.1rem,2.5vw,1.875rem)] font-extrabold ${
                f.delta > 0 ? "text-green-400" : "text-red-400"
              }`}
            >
              {f.delta > 0 ? "+" : ""}
              {f.delta.toLocaleString()}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-1 text-[clamp(0.85rem,1.6vw,1.25rem)] font-semibold uppercase tracking-widest text-accent md:mt-2">
        {rank}
      </div>
      {hasCrown && (
        <div className="mt-1.5 inline-block rounded-full border border-yellow-400/60 px-4 py-1 text-xs font-bold uppercase tracking-widest text-yellow-300 md:mt-3">
          👑 Crowned
        </div>
      )}
      {alignmentLabel && (
        <div
          className={`mt-1.5 inline-block rounded-full border px-4 py-1 text-xs font-bold uppercase tracking-widest md:mt-3 ${
            alignmentLabel.toLowerCase().includes("villain")
              ? "border-red-500/50 text-red-400"
              : "border-accent text-accent"
          }`}
        >
          {alignmentLabel}
        </div>
      )}
    </div>
  );
}
